import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** The default board; a specific one is `/dashboard/:id` on the same component. */
const DASHBOARD_PATH = '/dashboard';

// =============================================================================
// Tracks whether the current page belongs to a flow that started on a dashboard,
// so pages can render context-sensitive affordances — e.g. a "Back to Dashboard"
// link only when the user actually arrived via a dashboard widget, not when they
// used the sidebar. Pure client-side history: nothing is persisted across reloads.
// =============================================================================
@Injectable({ providedIn: 'root' })
export class NavigationOriginService {
  private readonly router = inject(Router);

  private readonly _currentUrl = signal<string | null>(null);

  /**
   * Path of the dashboard the current flow started on, or null when it didn't
   * start on one.
   *
   * STICKY on purpose. A one-hop "previous URL" cannot survive a drill-down:
   * dashboard → Accounts → open a member → Back would leave the previous URL
   * pointing at the member, dropping the link while the user is still inside the
   * flow that began on the dashboard. Every page this renders on has a detail
   * route, so that round trip is the common path, not an edge case.
   */
  private readonly _dashboardOrigin = signal<string | null>(null);

  /** The page the dashboard linked to. The origin lives as long as we stay inside it. */
  private readonly _landingPath = signal<string | null>(null);

  /**
   * Where "Back to Dashboard" should go — the board the user actually left, which
   * is not always the default one. Path only: `ams-back-link` takes a routerLink
   * with no query-param input, and a dashboard URL carries no query state today.
   */
  readonly dashboardOrigin = this._dashboardOrigin.asReadonly();

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.record(e.urlAfterRedirects));
  }

  /** True when the current page belongs to a flow that started on a dashboard. */
  cameFromDashboard(): boolean {
    return this._dashboardOrigin() !== null;
  }

  private record(next: string): void {
    const current = this._currentUrl();
    this._currentUrl.set(next);
    // First navigation of the session — nothing was navigated away from.
    if (current === null) return;

    const from = this.pathOf(current);
    const to = this.pathOf(next);
    // Same-page navigations (filter/paging/sort rewriting the query string) aren't
    // leaving anywhere, so they must not disturb the origin.
    if (from === to) return;

    // Moving between boards (default ↔ /dashboard/:id) is not the start of a
    // flow — the user is still ON a dashboard, and reporting an origin there
    // would offer a "back to dashboard" link to someone already looking at one.
    if (this.isDashboard(to)) {
      this.clear();
      return;
    }

    if (this.isDashboard(from)) {
      // Starting a new flow. Remember WHICH board, so the link returns to the one
      // the user left rather than always the default.
      this._dashboardOrigin.set(from);
      this._landingPath.set(to);
      return;
    }

    const landing = this._landingPath();
    if (landing !== null && !this.sameBranch(to, landing)) {
      this.clear();
    }
  }

  private clear(): void {
    this._dashboardOrigin.set(null);
    this._landingPath.set(null);
  }

  /**
   * `/dashboard` and `/dashboard/:id` both count — they render the same board.
   * Matched on a segment boundary so `/dashboard-admin/...`, an unrelated area,
   * never does. (A bare `startsWith('/dashboard')` would wrongly match it.)
   */
  private isDashboard(path: string): boolean {
    return path === DASHBOARD_PATH || path.startsWith(`${DASHBOARD_PATH}/`);
  }

  /**
   * Both directions on purpose. Descending covers a list and its detail rows;
   * ASCENDING covers a widget that links straight at a detail page, where the
   * user's next move is up to the list — losing the link there would be the same
   * dead end the sticky origin exists to prevent.
   */
  private sameBranch(a: string, b: string): boolean {
    return this.isWithin(a, b) || this.isWithin(b, a);
  }

  /** The same page, or one nested under it. */
  private isWithin(path: string, root: string): boolean {
    return path === root || path.startsWith(`${root}/`);
  }

  /** Path only, with any trailing slash dropped so the `${root}/` tests hold. */
  private pathOf(url: string): string {
    const end = url.search(/[?#]/);
    const path = end === -1 ? url : url.slice(0, end);
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }
}
